import { useEffect, useState } from 'react';
import { usePostHog } from 'posthog-js/react';

import { PushValuePropSheet } from '@/components/notifications/PushValuePropSheet';
import { usePushSoftPromptEligibility } from '@/hooks/usePushSoftPrompt';
import { subscribeToPushPrompts, type PushPromptEvent } from '@/lib/pushPromptBus';

/**
 * App-wide host for the event-driven push prompts (`streak_started`,
 * `first_food_log`). Mounted once so a moment fired from a mutation or a
 * helper function can still surface a sheet, whichever page the user is on.
 *
 * Eligibility is re-checked at the moment itself, so cool-off, max-asks and
 * the OS-already-prompted rule are all honoured; a blocked moment is logged
 * with its trigger/surface so it stays measurable next to the dashboard ask.
 */
export function PushPromptHost() {
  const eligibility = usePushSoftPromptEligibility();
  const posthog = usePostHog();
  const [pending, setPending] = useState<PushPromptEvent | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => subscribeToPushPrompts(setPending), []);

  useEffect(() => {
    if (!pending) return;
    if (!eligibility.resolved) return; // wait for the async permission check
    if (!eligibility.eligible) {
      posthog?.capture('push_soft_prompt_blocked', {
        reason: eligibility.reason,
        trigger: pending.trigger,
        surface: pending.surface,
      });
      setPending(null);
      return;
    }
    // Small beat so the sheet doesn't collide with the celebration/toast
    // that accompanies the moment itself.
    const t = setTimeout(() => setOpen(true), 1500);
    return () => clearTimeout(t);
  }, [pending, eligibility.resolved, eligibility.eligible, eligibility.reason, posthog]);

  if (!pending) return null;

  return (
    <PushValuePropSheet
      open={open}
      onClose={() => {
        setOpen(false);
        setPending(null);
      }}
      trigger={pending.trigger}
      surface={pending.surface}
      headline={
        pending.trigger === 'streak_started'
          ? 'Keep your streak alive'
          : 'Stay on track between sessions'
      }
    />
  );
}
